import React, { useState } from "react";
import { Link } from "react-router-dom";
import TopNav from "../components/TopNav";
import AppShell from "../components/AppShell";
import SEO from "../components/SEO";
import Footer from "../components/Footer";
import { api, useAuth } from "../context/AuthContext";
import { MessageCircleQuestion, ChevronDown, Loader2 } from "lucide-react";

const FAQS = [
  {
    q: "What is Gumble Studio?",
    a: <>Gumble Studio is an independent casino game studio building math-first slot, burst, and card titles exclusively for <StakeLink />. Its games are provably fair and verified through Stake Engine / RGS.</>,
  },
  {
    q: "Which games has Gumble released?",
    a: <>As of 2026 the catalog counts 14 titles, including HERETIC, Money Hawk 3D, Eyes Wild Shut, Wanted: Ocean Drive, Money Hawk: Pro Staker and Dead Draw Showdown. <Link to="/#games" className="text-white underline decoration-white/20 underline-offset-2">See all games with RTP and max wins</Link>.</>,
  },
  {
    q: "What does RTP mean on a Gumble slot?",
    a: <>RTP (return to player) is the long-run average a game pays back — for example 96.50% means about $96.50 returned per $100 wagered over millions of rounds. Gumble publishes RTP, volatility, and max win for every title.</>,
  },
  {
    q: "What is volatility and why does it matter?",
    a: <>Volatility describes how spiky payouts are. Low-volatility games pay small wins often; very-high-volatility games like HERETIC or Dead Draw Showdown pay rarely but can hit 10,000x–56,000x. Match volatility to your bankroll, not your mood.</>,
  },
  {
    q: "How do Gumble challenges and crates work?",
    a: <>Link your Stake account, submit a bet of 100x or more on a Gumble game placed within the last 7 days, and you earn a crate (up to 3 per 24 hours). Open crates for Stake tip rewards that scale with your win.</>,
  },
  {
    q: "How do I link my Stake account?",
    a: <>Sign in with Discord, enter your Stake username, confirm your Stake profile is public, and the account links — verified automatically, or reviewed by an admin.</>,
  },
  {
    q: "What is a burst game?",
    a: <>A burst (crash-style) game has a multiplier that climbs until the round ends at a random point — cash out before it ends to win. Gumble&apos;s burst titles are Money Hawk 3D (skate tricks stack multipliers) and Dead Draw Showdown (gunslinger duels).</>,
  },
  {
    q: "What is the basic burst strategy?",
    a: <>No pattern predicts the end of a round. The workable approach is exit timing plus stake sizing: auto-cash part of the stake at modest multipliers (1.5x–2x) and let the rest run, with a hard stop after consecutive busts.</>,
  },
  {
    q: "Are Gumble games provably fair?",
    a: <>Yes. Every round can be independently verified from published seeds — the stated odds are the real odds. Fair does not mean profitable: the house edge still applies, so play within a budget.</>,
  },
  {
    q: "Where can I play Gumble games?",
    a: <>Exclusively on Stake. Every play link on this site carries the studio&apos;s affiliate tag so play supports the studio: <StakeLink label="play Gumble on Stake" />.</>,
  },
  {
    q: "Does Gumble build games for other studios?",
    a: <>Yes. Gumble takes on custom slot development, SFX and sound design, animations, math and RTP tuning, and Stake Engine integration for partners. See <Link to="/partner" className="text-white underline decoration-white/20 underline-offset-2">Partner with Gumble</Link>.</>,
  },
  {
    q: "How can streamers and affiliates work with Gumble?",
    a: <>Through early access, community crate bounties, affiliate links, and studio collabs — start at <Link to="/partner" className="text-white underline decoration-white/20 underline-offset-2">the partner page</Link>.</>,
  },
];

function StakeLink({ label = "Stake" }) {
  return (
    <a href="https://stake.com/fr/casino/group/gumble?c=gumble" target="_blank" rel="noreferrer sponsored" className="text-white underline decoration-white/20 underline-offset-2">
      {label}
    </a>
  );
}

export default function Faq() {
  const { user, loading } = useAuth();
  const [open, setOpen] = useState(0);

  if (loading) return <div className="min-h-screen flex items-center justify-center text-white/60"><Loader2 className="animate-spin" /></div>;

  const body = (
    <>
      <SEO
        title="FAQ — games, math, rewards, services"
        description="Direct answers about Gumble Studio games, RTP, volatility, challenges, and studio services."
        path="/faq"
      />
      <div className={`${user ? "pt-10 md:pt-14" : "pt-28"} px-6 md:px-10 max-w-3xl mx-auto pb-20`}>
        <div className="flex items-center gap-2 text-[12px] uppercase tracking-[0.25em] text-white/40">
          <MessageCircleQuestion className="w-3.5 h-3.5" /> FAQ
        </div>
        <h1 className="mt-2 text-4xl md:text-6xl font-black tracking-[-0.03em]">Questions, answered.</h1>
        <p className="mt-4 text-white/60 text-[15px] leading-relaxed">
          Direct answers about Gumble games, math, rewards, and studio services — written to be cited.
        </p>
        <div className="mt-10 space-y-3">
          {FAQS.map((f, i) => (
            <div key={i} className={`glass rounded-2xl overflow-hidden transition-colors ${open === i ? "glass-strong" : ""}`}>
              <button onClick={() => setOpen(open === i ? -1 : i)} className="w-full flex items-center justify-between gap-4 text-left px-6 py-5">
                <span className="text-[16px] font-semibold tracking-tight">{f.q}</span>
                <ChevronDown className={`w-4 h-4 shrink-0 text-white/50 transition-transform duration-300 ${open === i ? "rotate-180" : ""}`} />
              </button>
              {open === i && <div className="px-6 pb-6 text-[14.5px] text-white/70 leading-relaxed">{f.a}</div>}
            </div>
          ))}
        </div>
      </div>
      <Footer />
    </>
  );

  if (user) return <AppShell>{body}</AppShell>;
  return (
    <div className="relative min-h-screen text-white">
      <TopNav variant="app" />
      {body}
    </div>
  );
}
